const SHEET_CONTENT_SELECTOR = "[data-slot='sheet-content']";
export const SHEET_FORM_SCROLL_BODY_SELECTOR = "[data-sheet-form-scroll-body]";
const LIST_SCROLL_SELECTOR = "[data-scroll-container]";
/** Room for Driver popover + step chrome. */
const POPOVER_RESERVE_PX = 200;
const EDGE_PADDING_PX = 16;

type ScrollSnapshot = {
  el: HTMLElement;
  top: number;
};

let sheetScrollSnapshot: ScrollSnapshot | null = null;

function hasOverflowScrollStyle(el: HTMLElement): boolean {
  const {overflowY} = getComputedStyle(el);
  return overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";
}

function isScrollable(el: HTMLElement): boolean {
  if (!hasOverflowScrollStyle(el)) {
    return false;
  }
  return el.scrollHeight > el.clientHeight + 1;
}

function findSheetFormScrollBody(target: HTMLElement, sheetContent: HTMLElement): HTMLElement | null {
  const body = target.closest(SHEET_FORM_SCROLL_BODY_SELECTOR);
  if (body instanceof HTMLElement && sheetContent.contains(body)) {
    return body;
  }
  return null;
}

function findScrollContainer(target: HTMLElement): HTMLElement | null {
  const sheetContent = target.closest(SHEET_CONTENT_SELECTOR);
  if (sheetContent instanceof HTMLElement) {
    const sheetBody = findSheetFormScrollBody(target, sheetContent);
    if (sheetBody) {
      return sheetBody;
    }

    let node: HTMLElement | null = target;
    while (node && node !== sheetContent.parentElement) {
      if (isScrollable(node)) {
        return node;
      }
      node = node.parentElement;
    }

    if (isScrollable(sheetContent)) {
      return sheetContent;
    }
  }

  const listScroll = document.querySelector(LIST_SCROLL_SELECTOR);
  if (listScroll instanceof HTMLElement && listScroll.contains(target)) {
    return listScroll;
  }

  return null;
}

/** Max scroll — layout children first; fall back to scrollHeight when metrics lag. */
function getContentMaxScrollTop(container: HTMLElement): number {
  let contentBottom = 0;

  for (const child of Array.from(container.children)) {
    if (child instanceof HTMLElement) {
      contentBottom = Math.max(contentBottom, child.offsetTop + child.offsetHeight);
    }
  }

  const style = getComputedStyle(container);
  const paddingBottom = Number.parseFloat(style.paddingBottom) || 0;
  const layoutMax = Math.max(0, contentBottom + paddingBottom - container.clientHeight);
  const scrollMax = Math.max(0, container.scrollHeight - container.clientHeight);

  if (layoutMax > 0) {
    return scrollMax > 0 ? Math.min(scrollMax, layoutMax) : layoutMax;
  }

  return scrollMax;
}

function clampContainerScroll(container: HTMLElement) {
  const maxScroll = getContentMaxScrollTop(container);
  if (container.scrollTop > maxScroll + 1) {
    container.scrollTo({top: maxScroll, behavior: "auto"});
  }
}

/** Clamp one sheet form scroller — prevents empty space below pinned footers after resize/focus. */
export function clampSheetFormScrollBody(container: HTMLElement) {
  clampContainerScroll(container);
}

/** Clamp every open sheet form scroller (e.g. compliance bar height change). */
export function clampAllOpenSheetFormScrollBodies() {
  document.querySelectorAll(SHEET_FORM_SCROLL_BODY_SELECTOR).forEach((node) => {
    if (node instanceof HTMLElement) {
      clampContainerScroll(node);
    }
  });
}

function getPopoverReserves(popoverSide: "top" | "right" | "bottom" | "left" | "over") {
  switch (popoverSide) {
    case "top":
      return {
        reserveTop: POPOVER_RESERVE_PX + EDGE_PADDING_PX,
        reserveBottom: EDGE_PADDING_PX,
      };
    case "over":
      return {
        reserveTop: POPOVER_RESERVE_PX / 2 + EDGE_PADDING_PX,
        reserveBottom: POPOVER_RESERVE_PX / 2 + EDGE_PADDING_PX,
      };
    case "left":
    case "right":
      return {
        reserveTop: EDGE_PADDING_PX,
        reserveBottom: EDGE_PADDING_PX,
      };
    case "bottom":
    default:
      return {
        reserveTop: EDGE_PADDING_PX,
        reserveBottom: POPOVER_RESERVE_PX + EDGE_PADDING_PX,
      };
  }
}

function scrollWithinContainer(
  element: HTMLElement,
  container: HTMLElement,
  popoverSide: "top" | "right" | "bottom" | "left" | "over" = "bottom",
) {
  const containerRect = container.getBoundingClientRect();
  const elRect = element.getBoundingClientRect();
  const {reserveTop, reserveBottom} = getPopoverReserves(popoverSide);

  const visibleTop = containerRect.top + reserveTop;
  const visibleBottom = containerRect.bottom - reserveBottom;

  const maxScroll = getContentMaxScrollTop(container);

  if (elRect.top >= visibleTop && elRect.bottom <= visibleBottom) {
    clampContainerScroll(container);
    return;
  }

  let nextScroll = container.scrollTop;

  const overflowTop = visibleTop - elRect.top;
  const overflowBottom = elRect.bottom - visibleBottom;

  if (overflowTop > 0) {
    nextScroll -= overflowTop;
  } else if (overflowBottom > 0) {
    nextScroll += overflowBottom;
  }

  nextScroll = Math.max(0, Math.min(nextScroll, maxScroll));

  if (nextScroll !== container.scrollTop) {
    container.scrollTo({top: nextScroll, behavior: "auto"});
  }

  clampContainerScroll(container);
}

/**
 * Scrolls a tour target into view inside the sheet or list scroll container.
 * Uses the minimum scroll delta so we do not overshoot past the form end.
 */
export function scrollHelpTourTargetIntoView(
  target: Element | undefined | null,
  popoverSide: "top" | "right" | "bottom" | "left" | "over" = "bottom",
) {
  if (!(target instanceof HTMLElement)) {
    return;
  }

  const container = findScrollContainer(target);
  if (container) {
    scrollWithinContainer(target, container, popoverSide);
    return;
  }

  target.scrollIntoView({block: "nearest", inline: "nearest", behavior: "auto"});
}

/** Undo driver.js center-scroll overshoot inside sheet/list scrollers. */
export function clampHelpTourScrollForTarget(target: Element | undefined | null) {
  if (!(target instanceof HTMLElement)) {
    return;
  }

  const container = findScrollContainer(target);
  if (container) {
    clampContainerScroll(container);
  }
}

/** Snapshot sheet inner scroll once per tour so we can restore on close. */
export function snapshotSheetScrollForTour(target: Element | undefined | null) {
  if (sheetScrollSnapshot || !(target instanceof HTMLElement)) {
    return;
  }

  const container = findScrollContainer(target);
  if (container?.closest(SHEET_CONTENT_SELECTOR)) {
    sheetScrollSnapshot = {el: container, top: container.scrollTop};
  }
}

export function takeSheetScrollSnapshot(): ScrollSnapshot | null {
  const snapshot = sheetScrollSnapshot;
  sheetScrollSnapshot = null;
  return snapshot;
}

/**
 * driver.js calls scrollIntoView({block:"center"}) after onHighlightStarted.
 * Re-apply our scroll + clamp once that synchronous work finishes.
 */
export function reconcileHelpTourScrollAfterDriver(
  target: Element | undefined | null,
  popoverSide: "top" | "right" | "bottom" | "left" | "over" = "bottom",
) {
  clampHelpTourScrollForTarget(target);
  scrollHelpTourTargetIntoView(target, popoverSide);
  clampHelpTourScrollForTarget(target);
}
